import React from 'react';
import { BASE_ROOT } from '../_helpers/constants';

// Utilities
import utils from '../_helpers/utils';

const userContext = React.createContext({user: {}});

function login(username, password) {
  const options = {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({name: username, password: password})
  }

  return fetch(`${BASE_ROOT}/auth/login`, options)
    .then(res => {
      if (!res.ok) {
        const error = new Error(`Login failed with status ${res.status}`);
        error.status = res.status;
        throw error;
      }
      return res.json();
    })
    .then(
      result => {
        lastRefreshFailure = 0;
        localStorage.setItem('accessToken', result.accessToken);
        localStorage.setItem('refreshToken', result.refreshToken);
      }
    )
}

const logout = async () => {
  const options = {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token: localStorage.getItem('refreshToken') })
  }
  
  await fetch(`${BASE_ROOT}/auth/logout`, options)
    .then(res => res.text())
    .then(
      () => {
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
      }
    )
}

const refresh = async () => {
  const tokenBefore = localStorage.getItem('refreshToken');
  if (!tokenBefore) return false;

  const run = async () => {
    // The refresh token rotates: if another tab refreshed while we waited for the lock, we're done
    if (localStorage.getItem('refreshToken') !== tokenBefore) return true;

    const options = {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token: tokenBefore })
    }

    try {
      const res = await fetch(`${BASE_ROOT}/auth/refresh`, options);

      // Refresh token rejected: the session is over
      if (res.status === 401 || res.status === 403) {
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        authService.onSessionExpired?.();
        return false;
      }

      // Transient failure (5xx, 429, ...): keep the session, fail this attempt
      if (!res.ok) return false;

      const result = await res.json();
      localStorage.setItem('accessToken', result.accessToken);
      // Absent when another tab's rotation just happened; keep the newer one we have
      if (result.refreshToken) {
        localStorage.setItem('refreshToken', result.refreshToken);
      }
      return true;
    } catch {
      return false;
    }
  }

  // One refresh at a time across all tabs
  return typeof navigator !== 'undefined' && navigator.locks
    ? navigator.locks.request('apbdb-refresh', run)
    : run();
}

// Share one in-flight refresh between all callers; back off for a while after a transient failure
const REFRESH_BACKOFF_MS = 30000;
let refreshing = null;
let lastRefreshFailure = 0;
const refreshOnce = () => {
  if (refreshing) return refreshing;
  if (Date.now() - lastRefreshFailure < REFRESH_BACKOFF_MS) return Promise.resolve(false);

  refreshing = refresh()
    .then(ok => {
      if (!ok) lastRefreshFailure = Date.now();
      return ok;
    })
    .finally(() => { refreshing = null; });
  return refreshing;
}

function register(username, password, email) {
  const options = {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ name: username, password: password, email: email })
  }

  return fetch(`${BASE_ROOT}/auth/register`, options);
}

function getUser() {
  return utils.parseJwt(localStorage.getItem('refreshToken'));
}

function getAccessToken() {
  return utils.parseJwt(localStorage.getItem('accessToken'));
}

const checkAccessExpired = async () => {
  const user = utils.parseJwt(localStorage.getItem('accessToken'));
  if (!user) return;

  if (new Date().getTime() / 1000 > user.exp - 30) {
    await refreshOnce();
  }
}

const isAuthenticated = () => getAccessToken() !== null;

// fetch with the Bearer token; refreshes proactively and retries once on 401.
// options.headers must be a plain object.
const authFetch = async (endpoint, options = {}) => {
  await checkAccessExpired();

  const send = () => fetch(BASE_ROOT + endpoint, {
    ...options,
    headers: {
      ...options.headers,
      'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
    }
  });

  let res = await send();
  if (res.status === 401 && await refreshOnce()) {
    res = await send();
  }

  // Only a rejected refresh token (tokens cleared) ends the session
  if (res.status === 401 && !localStorage.getItem('refreshToken')) {
    authService.onSessionExpired?.();
  }

  return res;
}

const authenticatedRequest = endpoint => authFetch(endpoint).then(res => res.json());

export const authService = {
    login,
    logout,
    refresh,
    register,
    getUser,
    getAccessToken,
    checkAccessExpired,
    authFetch,
    authenticatedRequest,
    isAuthenticated,
    onSessionExpired: null,
    userContext
}